import {
  getProducts,
  getCartItemDesign,
  getSharedDesign,
  getFavoriteDesign,
  getOrderItemDesign,
} from "api";
import t from "../translations";
import axios from "axios";
import { query } from "utils/general";

const products = (store) => {
  const { dispatch } = store;

  store.on("@init", () => ({
    products: null,
    product: null,
    proboUploader: null,
    productOptions: [],
    proboCodes: [],
    productCategories: [],
  }));
  store.on("products/set", (_, products) => {
    return { products };
  });
  store.on("productCategories/set", (_, productCategories) => {
    return { productCategories };
  });

  store.on("product/setProboOptions", (_, productOptions) => {
    return { productOptions };
  });

  store.on("product/setProboCodes", (_, proboCodes) => {
    return { proboCodes };
  });

  store.on("product/setProboUploader", (_, proboUploader) => {
    return { proboUploader };
  });

  store.on("products/designMore", () => {
    store.dispatch("canvas/init");
    return {
      isProductSelectorOpened: true,
      product: null,
      productOptions: [],
      proboCode: [],
      proboUploader: null,
    };
  });

  store.on("product/set", ({ products }, product) => {
    const existingProduct = products.find((p) => product.id === p.id);
    if (!existingProduct) {
      console.error("Can't set product non existant", product, products);
      dispatch("notification/error", t.productNotExist);
      return;
    }
    return { product };
  });

  store.on("products/get", async () => {
    try {
      const { products, categories } = await getProducts();
      dispatch("products/set", products);
      dispatch("productCategories/set", categories);
    } catch (err) {
      dispatch("notification/error", err.message);
    }
  });

  store.on("product/init", async ({ canvas, isAdmin, products }) => {
    const variationId = query.getNumber("id");
    const productId = variationId
      ? variationId
      : parseInt(window.DeskpadData.productId, 10);
    if (!productId) {
      dispatch("productSelector/open");
      return;
    }
    const product = products?.find((p) => p.id === productId);
    if (!product) {
      dispatch("notification/error", t.productNotExist);
      dispatch("productSelector/open");
      return;
    }
    dispatch("product/set", product);
    if (product.proboProduct) {
      dispatch("productSelector/open");
      return;
    }
    if (!product.editorData) {
      dispatch("notification/error", t.invalidCanvasData);
      return;
    }

    const widthParam = query.getNumber("width");
    const heightParam = query.getNumber("height");
    try {
      canvas.DPE.persistent.loadFromBase64(product.editorData, () => {
        if (widthParam && heightParam && product.price.type !== "fixed") {
          dispatch("canvas/updateSize", {
            width: widthParam,
            height: heightParam,
          });
        } else {
          const { width, height } = product.size ?? {};
          if (width && height) {
            dispatch("canvas/updateSize", {
              width: width * 1000,
              height: height * 1000,
            });
          }
        }
        dispatch("canvas/checkEditorType");
      });
    } catch (err) {
      dispatch("notification/error", `Can't init product: ${err.message}`);
    }

    if (product.proboProduct) {
      dispatch("productSelector/open");
    }
  });
  store.on("init/app", ({ canvas, isAdmin }, productId) => {
    const cartItemId = query.get("cart");
    const orderItemId = query.get("orderItem");
    const sharedDesignId = query.get("design");
    const favoriteId = query.getNumber("favorite");

    const category = query.getCategory();
    if (category) {
      dispatch("productSelector/open");
    } else if (cartItemId) {
      dispatch("init/cartItem");
    } else if (orderItemId) {
      dispatch("init/orderItem");
    } else if (sharedDesignId) {
      dispatch("init/sharedDesign");
    } else if (favoriteId) {
      dispatch("init/favoriteDesign");
    } else {
      dispatch("product/init");
    }

    if (
      !cartItemId &&
      !orderItemId &&
      !sharedDesignId &&
      !favoriteId &&
      !isAdmin
    ) {
      const storage = canvas.DPE.persistent;
      if (storage.isUnsavedDataExists()) {
        dispatch("notification/unsavedEditor");
      } else {
        dispatch("canvas/autosave/on");
      }
    }
  });

  const initProductData = async (productId, proboCodes, canvasData) => {
    dispatch("canvas/autosave/off");
    const products = store.get().products;
    const product = products.find((p) => p.id === productId);
    if (!product) {
      throw new Error(t.productNotExist);
    }
    if (!canvasData) {
      throw new Error(t.invalidCanvasData);
    }
    dispatch("product/set", product);

    if (product?.proboProduct && proboCodes) {
      const resp = await axios.post("/wp-json/dpe/v1/probo/product-options", {
        products: [
          {
            code: product.linkedProboProduct,
            options: proboCodes,
          },
        ],
      });
      const respProduct = resp.data.products[0];
      dispatch("product/setProboCodes", proboCodes);
      dispatch("product/setProboOptions", respProduct["selected_options"]);
      dispatch("product/setProboUploader", respProduct["uploaders"][0]);
    }
  };

  const initFailed = (text, err) => {
    dispatch("notification/error", { text, err });
    dispatch("content/setType", null);
    dispatch("content/setId", null);
    dispatch("canvas/endLoad");
    dispatch("productSelector/open");
  };

  store.on("init/cartItem", async ({ canvas }, customId) => {
    dispatch("canvas/startLoad");
    try {
      const id = customId ?? query.get("cart");
      dispatch("cartId/set", id);
      dispatch("content/setType/cartItem");
      dispatch("content/setId", id);
      const cartItem = await getCartItemDesign(id);
      const { productId, canvasData, proboCodes } = cartItem;
      await initProductData(productId, proboCodes, canvasData);
      canvas.DPE.persistent.loadFromBase64(canvasData, () => {
        dispatch("notification/info", t.editingCartItem);
        dispatch("productSelector/close");
        dispatch("canvas/endLoad");
        dispatch("canvas/checkEditorType");
      });
    } catch (err) {
      initFailed(t.cantLoadCartItem, err);
    }
  });

  store.on("init/sharedDesign", async ({ canvas }, customId) => {
    dispatch("canvas/startLoad");
    try {
      const id = customId ?? query.get("design");
      const cartItem = await getSharedDesign(id);
      const { productId, canvasData, proboOptions } = cartItem;
      await initProductData(productId, proboOptions, canvasData);
      canvas.DPE.persistent.loadFromBase64(canvasData, () => {
        dispatch("notification/info", t.sharedDesignLoaded);
        dispatch("productSelector/close");
        dispatch("canvas/endLoad");
        dispatch("canvas/checkEditorType");
      });
    } catch (err) {
      initFailed(t.cannotLoadSharedDesign, err);
    }
  });

  store.on("init/favoriteDesign", async ({ canvas }, customId) => {
    dispatch("canvas/startLoad");
    try {
      const id = customId ?? query.get("favorite");
      dispatch("content/setType/favorite");
      dispatch("content/setId", id);
      const data = await getFavoriteDesign(id);
      const { productId, canvasData, proboOptions } = data;
      await initProductData(productId, proboOptions, canvasData);
      canvas.DPE.persistent.loadFromBase64(canvasData, () => {
        dispatch("notification/info", t.editingFavoriteDesign);
        dispatch("productSelector/close");
        dispatch("canvas/endLoad");
        dispatch("canvas/checkEditorType");
      });
    } catch (err) {
      initFailed(t.cannotLoadFavoriteDesign, err);
    }
  });

  store.on("init/orderItem", async ({ canvas }, customId) => {
    dispatch("canvas/startLoad");
    const isTemplate = query.get("type") === "template";
    try {
      const id = customId ?? query.get("orderItem");
      if (id === "new") {
        dispatch("content/setType/orderItem");
        dispatch("productSelector/open");
        dispatch("canvas/endLoad");
        dispatch("notification/info", t.creatingOrderItem);
        return;
      }
      if (!isTemplate) {
        dispatch("content/setType/orderItem");
        dispatch("content/setId", id);
      }
      const data = await getOrderItemDesign(id);
      const { productId, canvasData, proboOptions, count } = data;
      await initProductData(productId, proboOptions, canvasData);
      window.DeskpadData.count = count;
      canvas.DPE.persistent.loadFromBase64(canvasData, () => {
        if (isTemplate) {
          dispatch("notification/info", t.createdFromTemplate);
        } else {
          dispatch("notification/info", t.editingOrderItem);
        }
        dispatch("productSelector/close");
        dispatch("canvas/endLoad");
        dispatch("canvas/checkEditorType");
      });
    } catch (err) {
      initFailed(t.cannotLoadOrderItem, err);
    }
  });

  store.on("@init", () => ({
    contentType: null,
    contentId: null,
  }));

  store.on("content/setType/cartItem", () => {
    return { contentType: "cartItem" };
  });

  store.on("content/setType/favorite", () => {
    return { contentType: "favorite" };
  });

  store.on("content/setType/orderItem", () => {
    return { contentType: "orderItem" };
  });

  store.on("content/setType", (_, contentType) => {
    return { contentType };
  });

  store.on("content/setId", (_, contentId) => {
    return { contentId };
  });

  store.on("@init", () => ({
    canvasLoading: false,
  }));

  store.on("canvas/startLoad", () => {
    return { canvasLoading: true };
  });

  store.on("canvas/endLoad", () => {
    return { canvasLoading: false };
  });
};

export default products;
